import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import {
  Compass,
  ArrowRight,
  Play,
  Check,
  Sparkles,
  BookOpen,
  Clock,
  X,
  ChevronDown,
  Award,
  Users,
  Globe,
  RefreshCw,
  Send,
  ArrowUpRight
} from "lucide-react";
import "@/App.css";

// Unsplash premium curated lifestyle images
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?auto=format&fit=crop&q=80&w=1000",
  manifesto1: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&q=80&w=600",
  manifesto2: "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&q=80&w=600",
  manifesto3: "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&q=80&w=600",
  manifesto4: "https://images.unsplash.com/photo-1444464666168-49d633b86797?auto=format&fit=crop&q=80&w=600",
  author: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=400",
  ebook: "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?auto=format&fit=crop&q=80&w=600"
};

export default function App() {
  // Smooth Momentum Scrolling with Lenis
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.4,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      wheelMultiplier: 1,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  // 15-Minute Resetting Cohort Countdown Timer
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds
  useEffect(() => {
    const storedStart = localStorage.getItem("countdown_start_time");
    let startTime = Date.now();

    if (storedStart) {
      const elapsed = Math.floor((Date.now() - parseInt(storedStart, 10)) / 1000);
      if (elapsed < 900) {
        setTimeLeft(900 - elapsed);
        startTime = parseInt(storedStart, 10);
      } else {
        localStorage.setItem("countdown_start_time", Date.now().toString());
        setTimeLeft(900);
      }
    } else {
      localStorage.setItem("countdown_start_time", Date.now().toString());
    }

    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      if (elapsed >= 900) {
        startTime = Date.now();
        localStorage.setItem("countdown_start_time", startTime.toString());
        setTimeLeft(900);
      } else {
        setTimeLeft(900 - elapsed);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // 5-Second Delayed eBook Popup Modal
  const [showEbook, setShowEbook] = useState(false);
  const [ebookEmail, setEbookEmail] = useState("");
  const [ebookSubmitted, setEbookSubmitted] = useState(false);

  useEffect(() => {
    const closed = sessionStorage.getItem("ebook_popup_closed");
    if (!closed) {
      const timer = setTimeout(() => {
        setShowEbook(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const closeEbook = () => {
    setShowEbook(false);
    sessionStorage.setItem("ebook_popup_closed", "true");
  };

  const handleEbookSubmit = (e) => {
    e.preventDefault();
    if (ebookEmail) {
      setEbookSubmitted(true);
      setTimeout(() => {
        closeEbook();
      }, 2500);
    }
  };

  // Self-Leadership Archetype Quiz state
  const [quizStep, setQuizStep] = useState(0); // 0 = intro, 1, 2, 3 = questions, 4 = result
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);

  const quizQuestions = [
    {
      q: "What is the primary bottleneck in your current coaching, healing, or therapeutic practice?",
      options: [
        { text: "I jump between different modalities (Reiki, NLP, breathwork), feeling scattered.", value: "kinetic" },
        { text: "I struggle to charge premium rates or position my work as a premium authority.", value: "sovereign" },
        { text: "I lack a consistent client pipeline and structured business systems.", value: "analytical" }
      ]
    },
    {
      q: "How do you currently package and deliver your transformational services?",
      options: [
        { text: "I charge hourly/per-session and feel physically and emotionally drained.", value: "kinetic" },
        { text: "I don't have a clear, high-ticket signature transformation model yet.", value: "sovereign" },
        { text: "I package them into programs, but have zero automated business systems.", value: "analytical" }
      ]
    },
    {
      q: "What does your professional growth and skill building focus on?",
      options: [
        { text: "Acquiring more certificates and modalities, hoping it solves my positioning gap.", value: "kinetic" },
        { text: "Learning how to confidently step up and hold authority as an elite, high-ticket mentor.", value: "sovereign" },
        { text: "Trying to stitch together complex funnel tech without a clear business blueprint.", value: "analytical" }
      ]
    }
  ];

  const handleQuizAnswer = (value) => {
    const nextAnswers = [...quizAnswers, value];
    setQuizAnswers(nextAnswers);
    if (quizStep < 3) {
      setQuizStep(quizStep + 1);
    } else {
      // Calculate archetype based on frequency
      const counts = nextAnswers.reduce((acc, curr) => {
        acc[curr] = (acc[curr] || 0) + 1;
        return acc;
      }, {});
      const topVal = Object.keys(counts).reduce((a, b) => counts[a] > counts[b] ? a : b);

      let archetype = {
        title: "The Undervalued Authority",
        badge: "Positioning Gap",
        desc: "You possess high-frequency expertise and deep wisdom, but you are charging commodity prices. Your main gap is authority-tier positioning and quantum-level high-ticket sales frameworks.",
        quote: "People do not buy what you do; they buy the transformation you command.",
        advise: "Focus on NQH Signature Premium Positioning and Healer Accelerator Masterminds."
      };
      if (topVal === "kinetic") {
        archetype = {
          title: "The Scattered Specialist",
          badge: "Modality Overloaded",
          desc: "You are exceptionally skilled in multiple therapeutic modalities (Reiki, NLP, breathwork), but struggle with diluted positioning. You need a unified, signature method to command high-ticket authority.",
          quote: "Complexity is the enemy of execution; simplicity commands respect.",
          advise: "Focus on the 120-Day NQH Healer Accelerator to build your signature premium practice."
        };
      } else if (topVal === "analytical") {
        archetype = {
          title: "The Hustling Solo-Practitioner",
          badge: "System Bottlenecked",
          desc: "You have a great method but lack standard business operations, a consistent client pipeline, and automated systems. You are trading time for money and are highly prone to burnout.",
          quote: "Systems run the business, and people run the systems.",
          advise: "Focus on deploying automated client pipelines and business systems in our NQH training."
        };
      }
      setQuizResult(archetype);
      setQuizStep(4);
    }
  };

  const restartQuiz = () => {
    setQuizStep(0);
    setQuizAnswers([]);
    setQuizResult(null);
  };

  // Testimonials Filtering State
  const [activeCategory, setActiveCategory] = useState("all");
  const testimonials = [
    {
      name: "Priya Sharma",
      role: "Clinical Hypnotherapist & Coach",
      category: "mindset",
      quote: "The Healer Accelerator completely transformed my business. I went from scattered hourly sessions to a premium signature program. My clients get quantifiable results now!",
      avatar: IMAGES.author
    },
    {
      name: "David Chen",
      role: "Reiki & Energy Healer",
      category: "composure",
      quote: "Before Miindtraa, I struggled to explain my work scientifically. The NQH framework gave me the perfect scientific bridge to communicate quantum energy healing to corporate clients.",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=400"
    },
    {
      name: "Sarah Jenkins",
      role: "Somatic Therapist & Author",
      category: "career",
      quote: "I finally have a predictable client pipeline. Dr. Amaey and Pankaj didn't just teach me branding; they helped me deploy real business systems that saved me 15 hours a week.",
      avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=400"
    }
  ];

  const filteredTestimonials = activeCategory === "all"
    ? testimonials
    : testimonials.filter(t => t.category === activeCategory);

  // Accordion FAQs state
  const [faqOpen, setFaqOpen] = useState(null);
  const faqs = [
    {
      q: "Who is Miindtraa Academy designed for?",
      a: "The academy is specifically built for healing practitioners, therapists, NLP coaches, reiki healers, and spiritual guides who want to transition from scattered, low-paying sessions into structured premium authority practices."
    },
    {
      q: "What is the NQH (Neurochakra Quantum Healing) framework?",
      a: "NQH is our proprietary, science-backed system integrating 4 specific pillars: Mind & Emotions, Energy Healing, Spiritual Growth, and Manifestation & Purpose. It bridges clinical psychology with quantum somatic healing."
    },
    {
      q: "How much daily commitment is required?",
      a: "We respect your time. Implementing the NQH business systems and reviewing curriculum modules requires about 30 minutes a day, designed to fit cleanly alongside your active therapeutic clients."
    },
    {
      q: "Will I have direct access to Dr. Amaey & Pankaj?",
      a: "Yes! Members of the NQH Healer Accelerator participate in live bi-weekly masterclasses and interactive reviews with both Dr. Amaey and Pankaj, receiving custom signature-offer feedback."
    },
    {
      q: "How does the resetting countdown work?",
      a: "To ensure high tutor-to-student support, Accelerator cohort admissions close in intimate blocks. Enrolling before the timer reset instantly grants you our complimentary 'NQH Practitioner' digital workbook."
    }
  ];

  // Billing Cycle state (Monthly vs Annual)
  const [billingPeriod, setBillingPeriod] = useState("annual"); // "monthly" or "annual"

  // Spotlight card mouse tracking
  const spotlightRef = useRef(null);
  const handleSpotlightMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    card.style.setProperty("--x", `${x}px`);
    card.style.setProperty("--y", `${y}px`);
  };

  // Parallax Hero Mouse Tilt
  const heroCardRef = useRef(null);
  const handleHeroMouseMove = (e) => {
    const card = heroCardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left - width / 2;
    const mouseY = e.clientY - rect.top - height / 2;
    const rX = (mouseY / height) * -12;
    const rY = (mouseX / width) * 12;
    card.style.transform = `perspective(1000px) rotateX(${rX}deg) rotateY(${rY}deg) scale3d(1.02, 1.02, 1.02)`;
  };

  const handleHeroMouseLeave = () => {
    const card = heroCardRef.current;
    if (!card) return;
    card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
  };

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans selection:bg-primary/20 selection:text-foreground relative overflow-x-hidden noise-bg">
      
      {/* Dynamic Cohort Sticky Countdown Banner */}
      <div className="bg-primary text-primary-foreground py-2.5 px-4 text-center text-xs font-semibold tracking-wider uppercase flex items-center justify-center gap-3 relative z-50">
        <Sparkles className="w-4 h-4 animate-pulse text-accent" />
        <span>Healer Accelerator applications closing in</span>
        <span data-testid="cohort-countdown-timer" className="font-mono text-sm px-2 py-0.5 rounded bg-foreground/15 border border-primary-foreground/10">
          {formatTime(timeLeft)}
        </span>
        <span className="hidden sm:inline">• Unlock the &apos;Quantum Healing Blueprint&apos; today</span>
      </div>

      {/* FIXED GLASSMOPRHIC NAVIGATION */}
      <header data-testid="header-container" className="sticky top-0 left-0 right-0 z-40 bg-[#F9F8F6] md:bg-[#F9F8F6]/85 backdrop-blur-xl border-b border-stone-200 py-4 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex items-center justify-between">
          
          {/* Logo & Brand Identity */}
          <a href="#home" data-testid="nav-brand-logo" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center border border-foreground/10 group-hover:scale-105 transition-transform duration-300">
              <Compass className="w-5 h-5 text-foreground" />
            </div>
            <div className="flex flex-col text-left">
              <span className="font-serif text-lg font-bold tracking-tight text-foreground leading-tight">Miindtraa Academy</span>
              <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-widest leading-none">by Dr. Amaey & Pankaj</span>
            </div>
          </a>

          {/* Desktop Nav Links */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#home" data-testid="nav-link-home" className="text-muted-foreground hover:text-foreground text-sm font-semibold tracking-wide transition-colors relative py-1 group">
              Home
              <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-primary group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#manifesto" data-testid="nav-link-manifesto" className="text-muted-foreground hover:text-foreground text-sm font-semibold tracking-wide transition-colors relative py-1 group">
              Pillars
              <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-primary group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#quiz" data-testid="nav-link-quiz" className="text-muted-foreground hover:text-foreground text-sm font-semibold tracking-wide transition-colors relative py-1 group">
              Practitioner Quiz
              <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-primary group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#memberships" data-testid="nav-link-memberships" className="text-muted-foreground hover:text-foreground text-sm font-semibold tracking-wide transition-colors relative py-1 group">
              Offerings
              <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-primary group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#testimonials" data-testid="nav-link-testimonials" className="text-muted-foreground hover:text-foreground text-sm font-semibold tracking-wide transition-colors relative py-1 group">
              Testimonials
              <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-primary group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#faq" data-testid="nav-link-faq" className="text-muted-foreground hover:text-foreground text-sm font-semibold tracking-wide transition-colors relative py-1 group">
              FAQ
              <span className="absolute bottom-0 left-0 w-0 h-[1.5px] bg-primary group-hover:w-full transition-all duration-300"></span>
            </a>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="#memberships"
              data-testid="nav-join-button"
              className="px-6 py-2.5 rounded-full bg-foreground hover:bg-foreground/90 text-background text-xs font-bold tracking-wider uppercase flex items-center gap-2 hover:gap-3 transition-all duration-300"
            >
              Join Accelerator <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 rounded-lg text-foreground hover:text-primary transition-colors focus:outline-none"
            aria-label="Open Menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

      </header>

      {/* Mobile Navigation Panel with Framer Motion */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-50 bg-[#F9F8F6] p-6 flex flex-col justify-between md:hidden"
          >
            <div className="flex flex-col gap-6 text-left">
              <div className="flex items-center justify-between">
                <span className="font-serif text-xl font-bold tracking-tight">Miindtraa Academy</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="nav-mobile-close"
                  className="p-2 text-foreground hover:text-primary transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <nav className="flex flex-col gap-5 mt-8 text-2xl font-serif font-light">
                <a href="#home" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary transition-colors">Home</a>
                <a href="#manifesto" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary transition-colors">Pillars</a>
                <a href="#quiz" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary transition-colors">Practitioner Quiz</a>
                <a href="#memberships" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary transition-colors">Offerings</a>
                <a href="#testimonials" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary transition-colors">Testimonials</a>
                <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="hover:text-primary transition-colors">FAQ</a>
              </nav>
            </div>

            <div className="flex flex-col gap-4 mt-auto">
              <a
                href="#memberships"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full py-4 rounded-full bg-foreground text-background text-center font-bold text-xs tracking-wider uppercase flex items-center justify-center gap-2"
              >
                Join Accelerator <ArrowRight className="w-4 h-4" />
              </a>
              <p className="text-center text-xs text-muted-foreground font-medium">Cohort admissions close soon</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <main>
        
        {/* HERO SECTION WITH LARGE KINETIC MASK REVEAL */}
        <section id="home" className="relative min-h-screen bg-background pt-16 pb-20 md:py-32 flex items-center overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Content Column */}
            <div className="lg:col-span-7 flex flex-col items-start text-left">
              
              {/* Dynamic badge with signature reveal */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent border border-foreground/5 shadow-sm mb-6"
              >
                <span className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse"></span>
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                  Join the Healer Accelerator Cohort
                </span>
              </motion.div>

              {/* Kinetic Header Mask Reveal */}
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-foreground leading-[1.1] mb-6">
                <span className="mask-reveal">
                  <motion.span
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                    className="block"
                  >
                    Scale Your Practice Into
                  </motion.span>
                </span>
                <span className="mask-reveal text-primary italic">
                  <motion.span
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.25 }}
                    className="block"
                  >
                    A Premium Authority.
                  </motion.span>
                </span>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-base sm:text-lg lg:text-xl text-muted-foreground font-light leading-relaxed max-w-xl mb-10"
              >
                Go beyond scattered modalities. Build a structured, high-ticket healing or coaching practice with the science-backed NQH (Neurochakra Quantum Healing) framework.
              </motion.p>

              {/* Interactive action controls */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto"
              >
                <a
                  href="#memberships"
                  data-testid="hero-primary-cta"
                  className="px-8 py-4 rounded-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold text-xs tracking-wider uppercase text-center flex items-center justify-center gap-2 group shadow-lg shadow-primary/10 hover:shadow-primary/20 transition-all duration-300"
                >
                  Explore Offerings
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href="#quiz"
                  data-testid="hero-secondary-cta"
                  className="px-8 py-4 rounded-full bg-accent hover:bg-accent/80 text-foreground font-bold text-xs tracking-wider uppercase text-center flex items-center justify-center gap-2 transition-all duration-300 border border-foreground/5"
                >
                  Take Practitioner Quiz
                  <Sparkles className="w-4 h-4 text-primary" />
                </a>
              </motion.div>

              {/* Minimal social proof metrics */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.7 }}
                className="mt-12 pt-8 border-t border-foreground/5 grid grid-cols-3 gap-6 w-full max-w-lg"
              >
                <div>
                  <h4 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">1,200+</h4>
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-widest font-semibold mt-1">Healing Practitioners</p>
                </div>
                <div>
                  <h4 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">98%</h4>
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-widest font-semibold mt-1">Practice Growth</p>
                </div>
                <div>
                  <h4 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">24+</h4>
                  <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-widest font-semibold mt-1">Global Cohorts</p>
                </div>
              </motion.div>

            </div>

            {/* Right Media Column - 3D Interactive Parallax Frame */}
            <div className="lg:col-span-5 flex justify-center relative">
              <div className="absolute -top-12 -left-12 w-64 h-64 bg-secondary/15 rounded-full blur-3xl -z-10 pointer-events-none"></div>
              <div className="absolute -bottom-12 -right-12 w-64 h-64 bg-primary/10 rounded-full blur-3xl -z-10 pointer-events-none"></div>
              
              <div
                onMouseMove={handleHeroMouseMove}
                onMouseLeave={handleHeroMouseLeave}
                className="relative w-full max-w-sm sm:max-w-md aspect-[4/5] rounded-[2.5rem] bg-accent p-3 border border-foreground/5 shadow-2xl transition-all duration-300 ease-out cursor-pointer"
                style={{ transformStyle: "preserve-3d" }}
              >
                <div
                  ref={heroCardRef}
                  className="w-full h-full rounded-[2.2rem] overflow-hidden relative shadow-inner transition-transform duration-300 ease-out"
                >
                  <img
                    src={IMAGES.hero}
                    alt="Aesthetic workspace with books, tea, and warm morning light representing self-leadership"
                    className="w-full h-full object-cover select-none scale-105 group-hover:scale-100 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-foreground/10 to-transparent flex flex-col justify-end p-8 text-left">
                    <div className="translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                      <p className="text-xs text-accent font-bold uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                        <Award className="w-3.5 h-3.5 text-accent" /> Featured Method
                      </p>
                      <h3 className="font-serif text-2xl text-background font-light leading-tight">
                        &ldquo;Your healing practice is the bridge to quantum transformation.&rdquo;
                      </h3>
                      <p className="text-xs text-accent/80 font-medium mt-3">— DR. AMAEY & PANKAJ</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* SLOW EDITORIAL SLIDING MARQUEE */}
        <div className="border-y border-foreground/5 bg-accent/40 py-6 overflow-hidden relative select-none">
          <div className="animate-marquee whitespace-nowrap flex items-center gap-16 font-serif text-lg sm:text-xl lg:text-2xl font-light text-foreground/80 tracking-wider">
            <span>• NQH FRAMEWORK</span>
            <span>• EMOTIONAL COGNITIVE MASTERY</span>
            <span>• PREMIUM PRACTICE BUILDING</span>
            <span>• QUANTUM ENERGY HEALING</span>
            <span>• HIGH-TICKET POSITIONING</span>
            <span>• 120-DAY IMPLEMENTATION</span>
            <span>• SOVEREIGN SPACE HOLDING</span>
            <span>• MEASURABLE TRANSFORMATION</span>
            {/* Repeat for seamless infinite looping */}
            <span>• NQH FRAMEWORK</span>
            <span>• EMOTIONAL COGNITIVE MASTERY</span>
            <span>• PREMIUM PRACTICE BUILDING</span>
            <span>• QUANTUM ENERGY HEALING</span>
            <span>• HIGH-TICKET POSITIONING</span>
            <span>• 120-DAY IMPLEMENTATION</span>
            <span>• SOVEREIGN SPACE HOLDING</span>
            <span>• MEASURABLE TRANSFORMATION</span>
          </div>
        </div>

        {/* NUMBERED MANIFESTO CHAPTERS SECTION */}
        <section id="manifesto" className="py-24 bg-background border-b border-foreground/5">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            
            <div className="max-w-2xl text-left mb-16 sm:mb-24">
              <h2 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">NQH BUSINESS PILLARS</h2>
              <p className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-foreground">
                Four Business Pillars of a Premium Practice
              </p>
              <p className="text-muted-foreground mt-4 font-light">
                Discover the core systems required to move from scattered, hourly-based sessions to a high-ticket, science-backed healing authority.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              
              {/* Chapter 01 */}
              <motion.div
                whileHover={{ y: -6 }}
                onMouseMove={handleSpotlightMouseMove}
                className="spotlight-card bg-accent rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between h-[360px] transition-all duration-300"
                data-testid="pillar-card-0"
              >
                <div>
                  <span className="font-serif text-4xl italic text-primary/80 font-bold">01</span>
                  <h3 className="font-serif text-2xl font-bold text-foreground mt-4 mb-2">Mind & Emotions</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Cultivate profound emotional composure and cognitive mastery to confidently hold high-frequency therapeutic containers.
                  </p>
                </div>
                <div className="w-full h-24 rounded-2xl overflow-hidden mt-4">
                  <img src={IMAGES.manifesto1} alt="Person in silent contemplation looking at a mountain sunrise" className="w-full h-full object-cover" />
                </div>
              </motion.div>

              {/* Chapter 02 */}
              <motion.div
                whileHover={{ y: -6 }}
                onMouseMove={handleSpotlightMouseMove}
                className="spotlight-card bg-accent rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between h-[360px] transition-all duration-300"
                data-testid="pillar-card-1"
              >
                <div>
                  <span className="font-serif text-4xl italic text-primary/80 font-bold">02</span>
                  <h3 className="font-serif text-2xl font-bold text-foreground mt-4 mb-2">Energy Healing</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Integrate ancient energetic pathways with somatic quantum healing methods for deep, measurable transformation.
                  </p>
                </div>
                <div className="w-full h-24 rounded-2xl overflow-hidden mt-4">
                  <img src={IMAGES.manifesto2} alt="Sunlight streaming onto a stack of books and a morning tea" className="w-full h-full object-cover" />
                </div>
              </motion.div>

              {/* Chapter 03 */}
              <motion.div
                whileHover={{ y: -6 }}
                onMouseMove={handleSpotlightMouseMove}
                className="spotlight-card bg-accent rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between h-[360px] transition-all duration-300"
                data-testid="pillar-card-2"
              >
                <div>
                  <span className="font-serif text-4xl italic text-primary/80 font-bold">03</span>
                  <h3 className="font-serif text-2xl font-bold text-foreground mt-4 mb-2">Spiritual Growth</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Align with your sovereign purpose, establish clear boundaries, and step into your authority as a visionary teacher.
                  </p>
                </div>
                <div className="w-full h-24 rounded-2xl overflow-hidden mt-4">
                  <img src={IMAGES.manifesto3} alt="A hand writing in an open journal with elegant handwriting" className="w-full h-full object-cover" />
                </div>
              </motion.div>

              {/* Chapter 04 */}
              <motion.div
                whileHover={{ y: -6 }}
                onMouseMove={handleSpotlightMouseMove}
                className="spotlight-card bg-accent rounded-[2rem] p-8 border border-foreground/5 text-left flex flex-col justify-between h-[360px] transition-all duration-300"
                data-testid="pillar-card-3"
              >
                <div>
                  <span className="font-serif text-4xl italic text-primary/80 font-bold">04</span>
                  <h3 className="font-serif text-2xl font-bold text-foreground mt-4 mb-2">Business Systems</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed font-light">
                    Deploy predictable client pipelines, premium packaging, and robust practice automation to scale without burnout.
                  </p>
                </div>
                <div className="w-full h-24 rounded-2xl overflow-hidden mt-4">
                  <img src={IMAGES.manifesto4} alt="Minimal forest scene with dense green pine trees and low hanging fog" className="w-full h-full object-cover" />
                </div>
              </motion.div>

            </div>

          </div>
        </section>

        {/* INTERACTIVE SELF-LEADERSHIP ARCHETYPE QUIZ SECTION */}
        <section id="quiz" className="py-24 bg-accent/40 border-b border-foreground/5 relative">
          <div className="max-w-4xl mx-auto px-6 sm:px-8">
            
            <div className="text-center mb-16">
              <h2 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">INTERACTIVE DIAGNOSTIC</h2>
              <p className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-foreground">
                Reveal Your Practitioner Archetype
              </p>
              <p className="text-muted-foreground max-w-xl mx-auto mt-4 font-light">
                Discover your practice's biggest bottleneck and receive a customized, step-by-step path to command premium authority.
              </p>
            </div>

            {/* Quiz Board Container */}
            <div data-testid="quiz-card" className="bg-background rounded-[2.5rem] p-8 sm:p-12 border border-foreground/5 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
              
              <AnimatePresence mode="wait">
                
                {/* Step 0: Welcome / Introduction */}
                {quizStep === 0 && (
                  <motion.div
                    key="step-0"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="text-center flex flex-col items-center py-6"
                  >
                    <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center mb-6 border border-foreground/5 shadow">
                      <Sparkles className="w-8 h-8 text-primary animate-pulse" />
                    </div>
                    <h3 className="font-serif text-3xl font-light text-foreground mb-4">What is stopping you from building a premium practice?</h3>
                    <p className="text-muted-foreground font-light max-w-md mb-8 leading-relaxed">
                      This brief 3-question evaluation identifies your primary practice bottleneck, positioning gaps, and provides a direct NQH action plan.
                    </p>
                    <button
                      onClick={() => setQuizStep(1)}
                      data-testid="quiz-option-0"
                      className="px-8 py-4 rounded-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold text-xs tracking-wider uppercase transition-all duration-300 flex items-center gap-2"
                    >
                      Begin Evaluation <ArrowRight className="w-4 h-4" />
                    </button>
                  </motion.div>
                )}

                {/* Steps 1, 2, 3: Active Questions */}
                {quizStep > 0 && quizStep <= 3 && (
                  <motion.div
                    key={`step-${quizStep}`}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="text-left"
                  >
                    {/* Progress Indicator */}
                    <div className="flex items-center justify-between mb-8">
                      <span className="text-xs font-bold uppercase tracking-wider text-primary">Question 0{quizStep} of 03</span>
                      <div className="w-32 bg-accent h-1.5 rounded-full overflow-hidden">
                        <div className="bg-primary h-full transition-all duration-300" style={{ width: `${(quizStep / 3) * 100}%` }}></div>
                      </div>
                    </div>

                    <h3 className="font-serif text-2xl sm:text-3xl text-foreground font-light leading-snug mb-8">
                      {quizQuestions[quizStep - 1].q}
                    </h3>

                    <div className="flex flex-col gap-4">
                      {quizQuestions[quizStep - 1].options.map((opt, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleQuizAnswer(opt.value)}
                          data-testid={`quiz-option-${idx}`}
                          className="w-full p-6 rounded-2xl bg-accent hover:bg-accent/80 border border-foreground/5 hover:border-primary/20 text-left flex items-center justify-between group transition-all duration-300 shadow-sm"
                        >
                          <span className="text-sm sm:text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                            {opt.text}
                          </span>
                          <div className="w-8 h-8 rounded-full bg-background border border-foreground/10 group-hover:bg-primary group-hover:border-primary flex items-center justify-center transition-all duration-300">
                            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary-foreground transition-colors" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Step 4: Diagnostic Result Output */}
                {quizStep === 4 && quizResult && (
                  <motion.div
                    key="step-4"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-left py-4"
                  >
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
                      <Award className="w-4 h-4 text-primary" />
                      <span className="text-xs font-bold text-primary uppercase tracking-wider">{quizResult.badge}</span>
                    </div>

                    <h3 className="font-serif text-4xl sm:text-5xl font-light text-foreground mb-4">
                      {quizResult.title}
                    </h3>

                    <p className="text-base sm:text-lg text-muted-foreground font-light leading-relaxed mb-6">
                      {quizResult.desc}
                    </p>

                    <div className="p-6 rounded-2xl bg-accent border-l-4 border-primary italic font-serif text-lg text-foreground mb-8">
                      &ldquo;{quizResult.quote}&rdquo;
                    </div>

                    <div className="p-6 rounded-2xl bg-accent border border-foreground/5 mb-8 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Check className="w-5 h-5 text-primary" />
                      </div>
                      <div className="text-left">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Actionable Priority</h4>
                        <p className="text-sm font-semibold text-foreground mt-0.5">{quizResult.advise}</p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <a
                        href="#memberships"
                        className="w-full sm:w-auto px-8 py-4 rounded-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold text-xs tracking-wider uppercase text-center transition-all duration-300"
                      >
                        Enroll with your Archetype
                      </a>
                      <button
                        onClick={restartQuiz}
                        data-testid="quiz-restart-button"
                        className="w-full sm:w-auto px-8 py-4 rounded-full bg-accent hover:bg-accent/80 text-foreground font-bold text-xs tracking-wider uppercase border border-foreground/5 flex items-center justify-center gap-2 transition-all duration-300"
                      >
                        <RefreshCw className="w-4 h-4 text-muted-foreground" /> Restart Diagnostic
                      </button>
                    </div>
                  </motion.div>
                )}

              </AnimatePresence>

            </div>

          </div>
        </section>

        {/* AUTHOR SPOTLIGHT PROFILE SECTION */}
        <section id="about" className="py-24 bg-background border-b border-foreground/5">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              
              {/* Profile Photo with spotlight clipping */}
              <div className="lg:col-span-5 flex justify-center">
                <div className="relative w-full max-w-sm aspect-[4/5] rounded-[2.5rem] bg-accent p-3 border border-foreground/5 shadow-xl">
                  <div className="w-full h-full rounded-[2.2rem] overflow-hidden relative">
                    <img src={IMAGES.author} alt="Dr. Amaey and Pankaj" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-transparent to-transparent flex flex-col justify-end p-8 text-left">
                      <p className="text-xs font-bold uppercase tracking-widest text-accent mb-1">FOUNDERS & GUIDES</p>
                      <h3 className="font-serif text-3xl text-background font-bold">Dr. Amaey & Pankaj</h3>
                      <p className="text-xs text-accent/80 mt-1">NQH Framework Creators</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bio Copy */}
              <div className="lg:col-span-7 text-left">
                <h2 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">THE CO-FOUNDERS</h2>
                <h3 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-foreground mb-6">
                  Build a High-Impact, Science-Backed Practice
                </h3>
                
                <p className="text-muted-foreground font-light leading-relaxed mb-6">
                  Dr. Amaey Parekh and Pankaj Sharad Nikam are the visionary co-founders of Miindtraa Academy and the creators of the NQH framework. Their work bridges the clinical precision of psychology and neuroscience with the profound, restorative power of energy healing.
                </p>

                <p className="text-muted-foreground font-light leading-relaxed mb-8">
                  They founded Miindtraa Academy to empower healing practitioners, therapists, and coaches to move beyond scattered, low-ticket modalities and build structured, highly impactful, premium practices that deliver quantifiable quantum transformation.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center border border-foreground/5">
                      <Award className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-serif text-lg font-bold text-foreground">The NQH Method</h4>
                      <p className="text-xs text-muted-foreground mt-1">Integrating clinical science with somatic energetic alignment.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center border border-foreground/5">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-serif text-lg font-bold text-foreground">120-Day Accelerator</h4>
                      <p className="text-xs text-muted-foreground mt-1">Deploy robust, high-ticket practice systems alongside top healers.</p>
                    </div>
                  </div>
                </div>

                <a
                  href="#memberships"
                  className="px-8 py-4 rounded-full bg-foreground hover:bg-foreground/90 text-background font-bold text-xs tracking-wider uppercase inline-flex items-center gap-2 hover:gap-3 transition-all duration-300"
                >
                  Join the Healer Accelerator <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>

            </div>
          </div>
        </section>

        {/* COMMUNITY MEMBERSHIPS PRICING TABLE */}
        <section id="memberships" className="py-24 bg-accent/40 border-b border-foreground/5">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            
            <div className="text-center mb-16">
              <h2 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">MEMBERSHIPS</h2>
              <h3 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-foreground">
                NQH Practice Investment Tiers
              </h3>
              <p className="text-muted-foreground max-w-xl mx-auto mt-4 font-light">
                Select your learning tier. Move from self-paced fundamental concepts to a fully guided, high-touch practice implementation.
              </p>

              {/* Billing Cycle Toggle */}
              <div className="inline-flex items-center gap-3 bg-background p-1.5 rounded-full border border-foreground/5 shadow mt-8">
                <button
                  onClick={() => setBillingPeriod("monthly")}
                  data-testid="pricing-billing-toggle"
                  className={`px-6 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${billingPeriod === "monthly" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingPeriod("annual")}
                  className={`px-6 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 flex items-center gap-1.5 ${billingPeriod === "annual" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
                >
                  Annually <span className="bg-accent text-foreground text-[10px] px-1.5 py-0.5 rounded-full font-extrabold">-20%</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-4xl mx-auto">
              
              {/* Tier 1: The Seed Tier */}
              <div className="bg-background rounded-[2.5rem] p-8 sm:p-12 border border-foreground/5 shadow-lg flex flex-col justify-between relative overflow-hidden text-left">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-xs font-extrabold text-muted-foreground uppercase tracking-widest">Self-Paced Foundations</span>
                    <span className="px-3 py-1 rounded-full bg-accent text-[10px] font-extrabold uppercase tracking-wide border border-foreground/5">NQH Foundations</span>
                  </div>

                  <h4 className="font-serif text-3xl font-bold text-foreground mb-4">The NQH Foundations</h4>
                  <p className="text-sm text-muted-foreground font-light leading-relaxed mb-8">
                    Gain immediate access to foundational NQH neuro-chakra alignment science, practitioner templates, and client positioning modules.
                  </p>

                  <div className="flex items-baseline gap-2 mb-8">
                    <span className="font-serif text-5xl font-bold text-foreground">
                      ${billingPeriod === "monthly" ? "39" : "29"}
                    </span>
                    <span className="text-xs text-muted-foreground font-semibold">/ month</span>
                  </div>

                  <hr className="border-foreground/5 my-6" />

                  <ul className="space-y-4 mb-10">
                    <li className="flex items-center gap-3 text-sm text-foreground/95">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span>Foundational NQH Science Video Lessons (12+ units)</span>
                    </li>
                    <li className="flex items-center gap-3 text-sm text-foreground/95">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span>Practitioner Positioning Blueprints & Checklists</span>
                    </li>
                    <li className="flex items-center gap-3 text-sm text-foreground/95">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span>Bi-weekly NQH Practitioner Research Letter</span>
                    </li>
                  </ul>
                </div>

                <a
                  href="#quiz"
                  data-testid="membership-join-seed"
                  className="w-full py-4 rounded-full bg-accent hover:bg-accent/80 text-foreground font-bold text-xs tracking-wider uppercase text-center border border-foreground/5 transition-colors"
                >
                  Acquire Foundations Access
                </a>
              </div>

              {/* Tier 2: The Sovereign Tier (Recommended) */}
              <div className="bg-background rounded-[2.5rem] p-8 sm:p-12 border-2 border-primary shadow-2xl flex flex-col justify-between relative overflow-hidden text-left">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-[10px] font-extrabold uppercase tracking-widest px-6 py-2 rounded-bl-[1.5rem] shadow-sm">
                  Recommended
                </div>

                <div>
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-xs font-extrabold text-primary uppercase tracking-widest">120-Day Cohort</span>
                    <span className="px-3 py-1 rounded-full bg-primary/10 text-[10px] font-extrabold text-primary uppercase tracking-wide border border-primary/20">Healer Accelerator Cohort</span>
                  </div>

                  <h4 className="font-serif text-3xl font-bold text-foreground mb-4">Healer Accelerator</h4>
                  <p className="text-sm text-muted-foreground font-light leading-relaxed mb-8">
                    Our signature 120-day high-touch implementation cohort. Direct live mastermind calls with Dr. Amaey & Pankaj, positioning custom reviews, and business system templates.
                  </p>

                  <div className="flex items-baseline gap-2 mb-8">
                    <span className="font-serif text-5xl font-bold text-foreground">
                      ${billingPeriod === "monthly" ? "129" : "99"}
                    </span>
                    <span className="text-xs text-muted-foreground font-semibold">/ month</span>
                  </div>

                  <hr className="border-foreground/5 my-6" />

                  <ul className="space-y-4 mb-10">
                    <li className="flex items-center gap-3 text-sm text-foreground/95">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span className="font-semibold">Direct live bi-weekly masterclasses with Dr. Amaey & Pankaj</span>
                    </li>
                    <li className="flex items-center gap-3 text-sm text-foreground/95">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span>Custom feedback on your high-ticket positioning & signatures</span>
                    </li>
                    <li className="flex items-center gap-3 text-sm text-foreground/95">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span>Exclusive NQH Practitioner Community Forum Access</span>
                    </li>
                    <li className="flex items-center gap-3 text-sm text-foreground/95">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span>Automated Client-Pipeline Deployment & Tech Workshops</span>
                    </li>
                  </ul>
                </div>

                <a
                  href="#quiz"
                  data-testid="membership-join-sovereign"
                  className="w-full py-4 rounded-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold text-xs tracking-wider uppercase text-center shadow-lg shadow-primary/15 transition-all duration-300"
                >
                  Acquire Accelerator Access
                </a>
              </div>

            </div>

          </div>
        </section>

        {/* FILTERABLE TESTIMONIALS SECTION */}
        <section id="testimonials" className="py-24 bg-background border-b border-foreground/5">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            
            <div className="text-center mb-16">
              <h2 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">EXPERIENCES</h2>
              <h3 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-foreground">
                Quantified Transformation
              </h3>
              <p className="text-muted-foreground max-w-xl mx-auto mt-4 font-light">
                Read the inspiring stories of therapists, healers, and coaches who transitioned to high-ticket structured authority.
              </p>

              {/* Testimonial Filter Toggle Buttons */}
              <div className="flex flex-wrap justify-center items-center gap-2 mt-10">
                <button
                  onClick={() => setActiveCategory("all")}
                  data-testid="testimonial-filter-all"
                  className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeCategory === "all" ? "bg-primary text-primary-foreground" : "bg-accent text-muted-foreground hover:text-foreground"}`}
                >
                  All
                </button>
                <button
                  onClick={() => setActiveCategory("mindset")}
                  data-testid="testimonial-filter-mindset"
                  className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeCategory === "mindset" ? "bg-primary text-primary-foreground" : "bg-accent text-muted-foreground hover:text-foreground"}`}
                >
                  Mindset
                </button>
                <button
                  onClick={() => setActiveCategory("composure")}
                  data-testid="testimonial-filter-composure"
                  className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeCategory === "composure" ? "bg-primary text-primary-foreground" : "bg-accent text-muted-foreground hover:text-foreground"}`}
                >
                  Energy Alignment
                </button>
                <button
                  onClick={() => setActiveCategory("career")}
                  data-testid="testimonial-filter-habits"
                  className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeCategory === "career" ? "bg-primary text-primary-foreground" : "bg-accent text-muted-foreground hover:text-foreground"}`}
                >
                  Business & Pipelines
                </button>
              </div>
            </div>

            {/* Testimonials List Grid with Motion */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto text-left">
              <AnimatePresence mode="popLayout">
                {filteredTestimonials.map((t, index) => (
                  <motion.div
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.4 }}
                    key={t.name}
                    className="bg-accent rounded-[2rem] p-8 border border-foreground/5 shadow flex flex-col justify-between h-[340px]"
                  >
                    <div>
                      <div className="flex items-center gap-1.5 text-primary mb-6">
                        <Sparkles className="w-4 h-4 fill-primary" />
                        <Sparkles className="w-4 h-4 fill-primary" />
                        <Sparkles className="w-4 h-4 fill-primary" />
                      </div>
                      <p className="text-base text-foreground font-serif font-light leading-relaxed italic mb-8">
                        &ldquo;{t.quote}&rdquo;
                      </p>
                    </div>

                    <div className="flex items-center gap-4 border-t border-foreground/5 pt-6">
                      <div className="w-12 h-12 rounded-full overflow-hidden border border-foreground/10">
                        <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <h5 className="font-serif text-lg font-bold text-foreground leading-tight">{t.name}</h5>
                        <p className="text-xs text-muted-foreground font-medium mt-0.5">{t.role}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

          </div>
        </section>

        {/* ACCORDION FAQ SECTION */}
        <section id="faq" className="py-24 bg-accent/40 border-b border-foreground/5">
          <div className="max-w-3xl mx-auto px-6 sm:px-8">
            
            <div className="text-center mb-16">
              <h2 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">RESOLUTIONS</h2>
              <h3 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-foreground">
                Inquiries & Insights
              </h3>
              <p className="text-muted-foreground mt-3 font-light">
                Everything you need to know about the structure, commitment, and alignment of the club.
              </p>
            </div>

            {/* Accordion List */}
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-background rounded-2xl border border-foreground/5 overflow-hidden shadow-sm">
                  <button
                    onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                    data-testid={`faq-trigger-${index}`}
                    className="w-full px-6 py-5 text-left flex items-center justify-between group transition-colors"
                  >
                    <span className="font-serif text-lg sm:text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                      {faq.q}
                    </span>
                    <motion.div
                      animate={{ rotate: faqOpen === index ? 180 : 0 }}
                      transition={{ duration: 0.25 }}
                      className="w-8 h-8 rounded-full bg-accent flex items-center justify-center"
                    >
                      <ChevronDown className="w-4 h-4 text-foreground" />
                    </motion.div>
                  </button>

                  <AnimatePresence initial={false}>
                    {faqOpen === index && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: "auto" }}
                        exit={{ height: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 text-sm sm:text-base text-muted-foreground font-light leading-relaxed border-t border-foreground/5 pt-4 text-left">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

          </div>
        </section>

      </main>

      {/* FOOTER SECTION */}
      <footer className="bg-foreground text-background py-16 px-6 sm:px-8 text-left relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-primary/10 rounded-full blur-3xl pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 border-b border-background/10 pb-12 mb-12">
          
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center border border-background/10">
                <Compass className="w-5 h-5 text-background" />
              </div>
              <span className="font-serif text-2xl font-bold tracking-tight text-background">Miindtraa Academy</span>
            </div>
            <p className="text-background/70 font-light leading-relaxed max-w-md mb-8">
              A premium professional academy and science-backed somatic framework designed to transition healing practitioners from modality-focused solo work to high-ticket, automated practice authority.
            </p>
            <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-widest text-background/50">
              <span className="flex items-center gap-1.5"><Globe className="w-4 h-4" /> Based Globally</span>
              <span>•</span>
              <span>© 2026 Miindtraa Academy</span>
            </div>
          </div>

          {/* Quick Newsletter form */}
          <div className="lg:col-span-6 flex flex-col justify-center">
            <h4 className="font-serif text-xl text-background font-semibold mb-3">Receive weekly quantum healing insights</h4>
            <p className="text-background/60 text-sm font-light mb-6">Science-backed somatic practices, high-ticket strategies, and practitioner resources directly to your inbox.</p>
            
            <form onSubmit={(e) => { e.preventDefault(); alert("Successfully joined newsletter circle."); }} className="flex flex-col sm:flex-row items-stretch gap-3 max-w-md">
              <input
                type="email"
                required
                placeholder="Enter your email address"
                data-testid="footer-newsletter-input"
                className="px-5 py-3.5 rounded-full bg-background/5 border border-background/10 text-background placeholder:text-background/40 focus:outline-none focus:border-primary transition-colors text-sm w-full"
              />
              <button
                type="submit"
                data-testid="footer-newsletter-submit"
                className="px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-bold text-xs tracking-wider uppercase flex items-center justify-center gap-2 hover:bg-primary/95 transition-all duration-300"
              >
                Subscribe <Send className="w-3.5 h-3.5" />
              </button>
            </form>
          </div>

        </div>

        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between text-xs text-background/45 font-medium tracking-wide">
          <div className="flex items-center gap-6 mb-4 sm:mb-0">
            <a href="#home" className="hover:text-background transition-colors">Privacy Policy</a>
            <a href="#home" className="hover:text-background transition-colors">Terms of Use</a>
            <a href="#home" className="hover:text-background transition-colors">Cookie Configurations</a>
          </div>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="scroll-to-top-button"
            className="px-4 py-2 rounded-full border border-background/10 hover:border-background text-background/60 hover:text-background flex items-center gap-2 transition-all duration-300"
          >
            Back to Top <ChevronDown className="w-4 h-4 rotate-180" />
          </button>
        </div>
      </footer>

      {/* 5-SECOND DELAYED COMPLIMENTARY EBOOK POPUP DIALOG */}
      <AnimatePresence>
        {showEbook && (
          <div data-testid="ebook-dialog" className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-foreground/50 backdrop-blur-md">
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-background rounded-[2.5rem] max-w-2xl w-full border border-foreground/5 overflow-hidden shadow-2xl relative grid grid-cols-1 md:grid-cols-12"
            >
              {/* Close Button */}
              <button
                onClick={closeEbook}
                data-testid="ebook-close-button"
                className="absolute top-4 right-4 p-2.5 rounded-full bg-background border border-foreground/5 text-foreground hover:text-primary hover:shadow transition-all duration-300 z-10"
                aria-label="Close dialogue"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Cover Column */}
              <div className="md:col-span-5 bg-accent relative overflow-hidden hidden md:block">
                <img src={IMAGES.ebook} alt="Open vintage book on wooden table in warm atmospheric room" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-primary/10"></div>
              </div>

              {/* Form Content Column */}
              <div className="md:col-span-7 p-8 sm:p-10 text-left flex flex-col justify-center">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4 w-fit">
                  <BookOpen className="w-3.5 h-3.5 text-primary" />
                  <span className="text-[10px] font-extrabold text-primary uppercase tracking-wider">Complimentary Copy</span>
                </div>

                <h4 className="font-serif text-3xl font-light text-foreground mb-3 leading-tight">
                  The Science of Quantum Healing Blueprint
                </h4>
                
                <p className="text-muted-foreground text-sm font-light leading-relaxed mb-6">
                  Unlock our 40-page blueprint containing somatic alignment guides, high-ticket practitioner templates, and quantum practice systems.
                </p>

                <AnimatePresence mode="wait">
                  {!ebookSubmitted ? (
                    <motion.form
                      key="form"
                      onSubmit={handleEbookSubmit}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-3"
                    >
                      <input
                        type="email"
                        required
                        value={ebookEmail}
                        onChange={(e) => setEbookEmail(e.target.value)}
                        placeholder="Enter your primary email"
                        data-testid="ebook-email-input"
                        className="px-5 py-3.5 rounded-full bg-accent border border-foreground/5 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary transition-colors text-sm w-full"
                      />
                      <button
                        type="submit"
                        data-testid="ebook-submit-button"
                        className="w-full py-4 rounded-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold text-xs tracking-wider uppercase flex items-center justify-center gap-2 shadow transition-colors"
                      >
                        Claim Quantum Healing Blueprint <ArrowRight className="w-4 h-4" />
                      </button>
                    </motion.form>
                  ) : (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      data-testid="ebook-success-message"
                      className="p-6 rounded-2xl bg-secondary/10 border border-secondary/20 text-center flex flex-col items-center"
                    >
                      <Check className="w-8 h-8 text-primary mb-2 animate-bounce" />
                      <h5 className="font-serif text-lg font-bold text-foreground">Sovereign Blueprint Sent</h5>
                      <p className="text-xs text-muted-foreground mt-1">Please verify your primary email shortly.</p>
                    </motion.div>
                  )}
                </AnimatePresence>
                
              </div>

            </motion.div>

          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
